'use client';
import type { ReactElement } from 'react';
import { motion } from 'framer-motion';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { ButtonPreset } from '@/constants/componentPresets';
import { Cbutton, type CbuttonProps } from '../Cbutton';
import { CONTROLS_ANIMATION, GLASSY_CARD_BACKGROUND_STYLE, HeroCarouselControlsAnimationEvent, type HeroCarouselControlsAnimationEventHandler, HeroCarouselControlsViewport, } from './Cherocarouselcontrols.constants';
import { notifyHeroCarouselControlsAnimationEvent, resolveHeroCarouselControlsArrowAnimation, resolveHeroCarouselControlsTransition, } from './Cherocarouselcontrols.motion';
export enum CarouselArrowControlDirection {
    Previous = 'previous',
    Next = 'next'
}
interface CarouselArrowControlProps {
    direction: CarouselArrowControlDirection;
    controlsViewport: HeroCarouselControlsViewport;
    shouldShowControls: boolean;
    controlsEntranceDelay: number;
    onClick?: () => void;
    onAnimationEvent?: HeroCarouselControlsAnimationEventHandler;
}
interface CarouselArrowControlConfig {
    dataName: string;
    initialX: number;
    hiddenX: number;
    icon: NonNullable<CbuttonProps['icon']>;
    paddingByViewport: Record<HeroCarouselControlsViewport, string>;
    animationStartEvent: HeroCarouselControlsAnimationEvent;
    animationCompleteEvent: HeroCarouselControlsAnimationEvent;
}
const ARROW_ICON_SIZE_BY_VIEWPORT: Record<HeroCarouselControlsViewport, number> = {
    [HeroCarouselControlsViewport.Mobile]: 24,
    [HeroCarouselControlsViewport.Desktop]: 28,
};
const ARROW_CONTROL_CONFIGS: Record<CarouselArrowControlDirection, CarouselArrowControlConfig> = {
    [CarouselArrowControlDirection.Previous]: {
        dataName: 'leftArrowWrapper',
        initialX: CONTROLS_ANIMATION.arrowDistance,
        hiddenX: CONTROLS_ANIMATION.arrowDistance,
        icon: IconChevronLeft,
        paddingByViewport: {
            [HeroCarouselControlsViewport.Mobile]: '15px',
            [HeroCarouselControlsViewport.Desktop]: '20px 60px 20px 20px',
        },
        animationStartEvent: HeroCarouselControlsAnimationEvent.LeftArrowStart,
        animationCompleteEvent: HeroCarouselControlsAnimationEvent.LeftArrowComplete,
    },
    [CarouselArrowControlDirection.Next]: {
        dataName: 'rightArrowWrapper',
        initialX: -CONTROLS_ANIMATION.arrowDistance,
        hiddenX: -CONTROLS_ANIMATION.arrowDistance,
        icon: IconChevronRight,
        paddingByViewport: {
            [HeroCarouselControlsViewport.Mobile]: '15px',
            [HeroCarouselControlsViewport.Desktop]: '20px 20px 20px 60px',
        },
        animationStartEvent: HeroCarouselControlsAnimationEvent.RightArrowStart,
        animationCompleteEvent: HeroCarouselControlsAnimationEvent.RightArrowComplete,
    },
};
export function CarouselArrowControl({ direction, controlsViewport, shouldShowControls, controlsEntranceDelay, onClick, onAnimationEvent, }: CarouselArrowControlProps): ReactElement {
    const config = ARROW_CONTROL_CONFIGS[direction];
    return <motion.div initial={{ opacity: 0, x: config.initialX }} animate={resolveHeroCarouselControlsArrowAnimation({
            shouldShowControls,
            hiddenX: config.hiddenX,
        })} onAnimationStart={() => {
            notifyHeroCarouselControlsAnimationEvent({
                onAnimationEvent,
                eventName: config.animationStartEvent,
            });
        }} onAnimationComplete={() => {
            notifyHeroCarouselControlsAnimationEvent({
                onAnimationEvent,
                eventName: config.animationCompleteEvent,
            });
        }} transition={resolveHeroCarouselControlsTransition({
            shouldShowControls,
            controlsEntranceDelay,
        })} style={{ display: 'flex' }}>
            <Cbutton rootAttributes={{ "data-el": "Cherocarouselcontrols/CarouselArrowControl/Trigger" }} preset={ButtonPreset.Secondary} icon={config.icon} iconSize={ARROW_ICON_SIZE_BY_VIEWPORT[controlsViewport]} onClick={onClick} padding={config.paddingByViewport[controlsViewport]} containerStyle={GLASSY_CARD_BACKGROUND_STYLE}/>
        </motion.div>
        ;
}
